import { z } from 'zod';
import { serviceSchema } from './system-health.js';
/**
 * Performance on Settings → System Health (TS-1070). Each API and worker instance keeps a
 * rolling window of request (or job tick) durations in fixed latency buckets and publishes
 * it; the API sums the buckets across instances and estimates percentiles from them. Fixed
 * buckets are what make that merge exact: percentiles themselves cannot be averaged.
 *
 * Pure (no Node APIs), so the API, the worker and their tests share one implementation.
 */
/** Upper bounds (ms) of the latency buckets; one more bucket holds everything slower. */
export const LATENCY_BUCKETS_MS = [5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000];
export const PERF_WINDOW_MINUTES = 15;
/** How often instances publish, and when a report is too old to count. */
export const PERF_REPORT_MS = 30_000;
export const PERF_REPORT_STALE_MS = 120_000;
export function emptyWindow() {
    return {
        count: 0,
        errors: 0,
        sumMs: 0,
        maxMs: 0,
        buckets: new Array(LATENCY_BUCKETS_MS.length + 1).fill(0),
    };
}
export function bucketIndex(ms) {
    const i = LATENCY_BUCKETS_MS.findIndex((bound) => ms <= bound);
    return i === -1 ? LATENCY_BUCKETS_MS.length : i;
}
export function addSample(w, ms, isError) {
    w.count += 1;
    if (isError)
        w.errors += 1;
    w.sumMs += ms;
    w.maxMs = Math.max(w.maxMs, ms);
    w.buckets[bucketIndex(ms)] += 1;
}
export function mergeWindows(a, b) {
    return {
        count: a.count + b.count,
        errors: a.errors + b.errors,
        sumMs: a.sumMs + b.sumMs,
        maxMs: Math.max(a.maxMs, b.maxMs),
        buckets: a.buckets.map((n, i) => n + (b.buckets[i] ?? 0)),
    };
}
/**
 * The q-quantile (0..1) estimated from the buckets: linear within the bucket it falls in,
 * capped at the slowest sample seen (so a p99 never exceeds the max). null with no samples.
 */
export function percentile(w, q) {
    if (w.count === 0)
        return null;
    const rank = Math.max(1, Math.ceil(q * w.count));
    let seen = 0;
    for (let i = 0; i < w.buckets.length; i += 1) {
        const n = w.buckets[i];
        if (n === 0)
            continue;
        if (seen + n >= rank) {
            const lower = i === 0 ? 0 : LATENCY_BUCKETS_MS[i - 1];
            const upper = i < LATENCY_BUCKETS_MS.length ? LATENCY_BUCKETS_MS[i] : w.maxMs;
            const estimate = lower + ((upper - lower) * (rank - seen)) / n;
            return Math.round(Math.min(estimate, w.maxMs) * 10) / 10;
        }
        seen += n;
    }
    return w.maxMs;
}
/**
 * The last `minutes` of samples per key, in one-minute slots. `snapshot()` merges the live
 * slots; older slots are dropped as time moves on, so memory stays bounded by keys × minutes.
 */
export class RollingWindow {
    minutes;
    slots = new Map();
    constructor(minutes = PERF_WINDOW_MINUTES) {
        this.minutes = minutes;
    }
    record(key, ms, isError, now = Date.now()) {
        const minute = Math.floor(now / 60_000);
        let slot = this.slots.get(minute);
        if (!slot) {
            slot = new Map();
            this.slots.set(minute, slot);
            this.prune(minute);
        }
        let w = slot.get(key);
        if (!w) {
            w = emptyWindow();
            slot.set(key, w);
        }
        addSample(w, ms, isError);
    }
    snapshot(now = Date.now()) {
        this.prune(Math.floor(now / 60_000));
        const out = {};
        for (const slot of this.slots.values())
            for (const [key, w] of slot)
                out[key] = out[key] ? mergeWindows(out[key], w) : mergeWindows(emptyWindow(), w);
        return out;
    }
    prune(currentMinute) {
        for (const minute of this.slots.keys())
            if (minute <= currentMinute - this.minutes)
                this.slots.delete(minute);
    }
}
/** One instance's process health, sampled when it publishes. */
export const runtimeStatsSchema = z.object({
    uptimeSeconds: z.number(),
    rssMb: z.number(),
    heapUsedMb: z.number(),
    heapTotalMb: z.number(),
    /** Event-loop delay since the previous report: how long ready work waited for the thread. */
    eventLoopP99Ms: z.number(),
    eventLoopMeanMs: z.number(),
});
const latencySummary = {
    count: z.number().int(),
    errors: z.number().int(),
    /** errors / count, 0..1. */
    errorRate: z.number(),
    avgMs: z.number().nullable(),
    p50Ms: z.number().nullable(),
    p95Ms: z.number().nullable(),
    p99Ms: z.number().nullable(),
    maxMs: z.number(),
};
export const performanceSchema = z.object({
    checkedAt: z.iso.datetime({ offset: true }),
    windowMinutes: z.number().int(),
    /** Every API request in the window, all instances. */
    requests: z.object({ ...latencySummary, perMinute: z.number() }),
    /** Per route template (`GET /candidates/:id`), slowest p95 first. */
    routes: z.array(z.object({ method: z.string(), route: z.string(), ...latencySummary })),
    /** The worker's loops: tick durations and failed ticks. */
    jobs: z.array(z.object({ job: z.string(), ...latencySummary })),
    instances: z.array(z.object({
        service: serviceSchema,
        instanceId: z.string(),
        reportedAt: z.iso.datetime({ offset: true }),
        runtime: runtimeStatsSchema,
    })),
});
/** The summary fields of `performanceSchema` for one merged window. */
export function summarise(w) {
    return {
        count: w.count,
        errors: w.errors,
        errorRate: w.count ? w.errors / w.count : 0,
        avgMs: w.count ? Math.round((w.sumMs / w.count) * 10) / 10 : null,
        p50Ms: percentile(w, 0.5),
        p95Ms: percentile(w, 0.95),
        p99Ms: percentile(w, 0.99),
        maxMs: Math.round(w.maxMs * 10) / 10,
    };
}
//# sourceMappingURL=performance.js.map